import { useCallback, useEffect, useRef, useState } from 'react';

import { Plus, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useConversations } from '@/hooks/useMessaging';

import { ConversationItem } from './ConversationItem';
import { NewConversationDialog } from './NewConversationDialog';

interface ConversationListProps {
  selectedId: string | undefined;
  onSelect: (id: string) => void;
}

export const ConversationList = ({ selectedId, onSelect }: ConversationListProps) => {
  const [search, setSearch] = useState('');
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useConversations({
    search: search || undefined,
  });

  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    return () => {
      observer.current?.disconnect();
    };
  }, []);

  const loadMoreRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isFetchingNextPage) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasNextPage) {
          fetchNextPage();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isFetchingNextPage, hasNextPage, fetchNextPage],
  );

  const conversations = data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <div className="flex h-full flex-col">
      <div className="border-b p-3">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Conversations</h2>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => setNewDialogOpen(true)}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <div className="relative">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search conversations..."
            className="pl-9"
          />
        </div>
      </div>

      <NewConversationDialog
        open={newDialogOpen}
        onOpenChange={setNewDialogOpen}
        onCreated={onSelect}
      />

      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="space-y-2 p-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 rounded-lg px-3 py-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-48" />
                </div>
              </div>
            ))}
          </div>
        ) : !conversations.length ? (
          <div className="text-muted-foreground flex h-40 items-center justify-center text-sm">
            {search ? 'No conversations match your search' : 'No conversations yet'}
          </div>
        ) : (
          <div className="space-y-0.5 p-1">
            {conversations.map((conversation) => (
              <ConversationItem
                key={conversation.id}
                conversation={conversation}
                isSelected={conversation.id === selectedId}
                onClick={() => onSelect(conversation.id)}
              />
            ))}
            {hasNextPage && (
              <div ref={loadMoreRef} className="flex justify-center py-3">
                {isFetchingNextPage ? (
                  <div className="border-primary h-5 w-5 animate-spin rounded-full border-b-2" />
                ) : (
                  <span className="text-muted-foreground text-xs">Scroll for more</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
